import React, { useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../context/auth';
import { Replica, Voice } from '../types/types';
import { Button, Select, TextInput } from './common';

export const avatarCardStyle: React.CSSProperties = {margin: '5px', borderRadius: '10px', backgroundColor: '#f2f2f2', padding: '15px'}

// VoiceInput picks an ElevenLabs voice from the list, or takes a voice id
// by hand when the list is empty (the TTS key may not be allowed to list
// voices).
export function VoiceInput({voices, value, onChange}: {voices: Voice[], value: string, onChange: (voiceId: string) => void}) {
  if (voices.length === 0) {
    return <TextInput style={{margin: 0}} placeholder="Voice ID" value={value} onChange={(evt) => onChange(evt.target.value)} />
  }
  return (
    <Select value={value} onChange={(evt) => onChange(evt.target.value)}>
      <option value="">Choose a voice</option>
      {/* Keep a voice id that isn't in the list (e.g. set by hand) selectable. */}
      {value && !voices.some(voice => voice.voice_id === value) ? <option value={value}>{value}</option> : null}
      {voices.map(voice => <option key={voice.voice_id} value={voice.voice_id}>{voice.name}</option>)}
    </Select>
  )
}

// CreateAvatarFromImage turns a still image (e.g. a cartoon or mascot)
// into a HeyGen talking-photo avatar that speaks with an ElevenLabs voice.
// endpoint is the customer or admin .../replicas/photo URL.
export function CreateAvatarFromImage({endpoint, voices, defaultShared, showSharing, onCreated, onCancel}: {
  endpoint: string,
  voices: Voice[],
  defaultShared: boolean,
  showSharing?: boolean,
  onCreated: (replica: Replica) => void,
  onCancel: () => void,
}) {
  const {authenticated} = useContext(AuthContext)
  const [image, setImage] = useState<File | null>(null)
  const [name, setName] = useState("")
  const [voiceId, setVoiceId] = useState("")
  const [shared, setShared] = useState(defaultShared)
  const [saving, setSaving] = useState(false)

  const preview = useMemo(() => image ? URL.createObjectURL(image) : "", [image])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const handleCreate = async () => {
    if (!image || !name || !voiceId) {
      alert("Please choose an image, a name and a voice.")
      return
    }
    const formData = new FormData()
    formData.append("image", image)
    formData.append("name", name)
    formData.append("tts_voice_id", voiceId)
    formData.append("shared_with_account", shared ? "true" : "false")

    setSaving(true)
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: {'Authorization': 'Bearer ' + authenticated.token}
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        onCreated(data.replica as Replica)
      } else {
        alert("Could not create the avatar" + (data.error ? `: ${data.error}` : ". Use a png or jpeg image under 10 MB."))
      }
    } catch (error) {
      console.error("Create avatar error:", error)
      alert("Error creating avatar")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{...avatarCardStyle, display: 'flex', flexDirection: 'column', gap: '10px', width: '250px', flexShrink: 0}}>
      <strong>New avatar from image</strong>
      <input type="file" accept="image/png,image/jpeg" onChange={(evt) => setImage(evt.target.files?.[0] ?? null)} />
      {preview ? <img src={preview} alt="Avatar preview" width={250} style={{borderRadius: '8px'}} /> : null}
      <TextInput style={{margin: 0}} placeholder="Name" value={name} onChange={(evt) => setName(evt.target.value)} />
      <VoiceInput voices={voices} value={voiceId} onChange={setVoiceId} />
      {showSharing ? <label>
        <input type="checkbox" checked={shared} onChange={(evt) => setShared(evt.target.checked)} /> Shared with whole account
      </label> : null}
      <div style={{display: 'flex', gap: '10px'}}>
        <Button $primary disabled={saving} onClick={handleCreate}>{saving ? "Creating..." : "Create"}</Button>
        <Button disabled={saving} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}
